import React, { useEffect, useState } from 'react';
import { useWorkflows } from '../../hooks/use-workflows';
import type { Workflow, WorkflowRun } from '../../lib/workflow-types';

interface Props {
  workflowId?: string;
  workflows: Workflow[];
  onClose: () => void;
}

export function WorkflowRunsDashboard({ workflowId, workflows, onClose }: Props) {
  const { runs, loading, error, fetchRuns } = useWorkflows();
  const [selectedRun, setSelectedRun] = useState<WorkflowRun | null>(null);

  useEffect(() => {
    fetchRuns(workflowId);
  }, [workflowId, fetchRuns]);

  const filteredRuns = workflowId ? runs.filter(r => r.workflowId === workflowId) : runs;

  if (loading) return <div className="p-8 text-center">Loading runs...</div>;
  if (error) return <div className="p-8 text-red-600">{error}</div>;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">
          {workflowId ? 'Workflow Runs' : 'All Runs'}
        </h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
      </div>

      {filteredRuns.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No runs yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3">Workflow</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Triggered By</th>
                <th className="text-left py-2 px-3">Started</th>
                <th className="text-left py-2 px-3">Duration</th>
                <th className="text-left py-2 px-3">Steps</th>
              </tr>
            </thead>
            <tbody>
              {filteredRuns.map(run => (
                <tr
                  key={run.id}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedRun(run)}
                >
                  <td className="py-2 px-3 font-medium">{run.workflowName}</td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      run.status === 'completed' ? 'bg-green-100 text-green-700' :
                      run.status === 'running' ? 'bg-blue-100 text-blue-700' :
                      run.status === 'failed' ? 'bg-red-100 text-red-700' :
                      run.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {run.status}
                    </span>
                  </td>
                  <td className="py-2 px-3">{run.triggeredBy}</td>
                  <td className="py-2 px-3">{new Date(run.startedAt).toLocaleString()}</td>
                  <td className="py-2 px-3">
                    {run.completedAt
                      ? `${Math.round((new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime()) / 1000)}s`
                      : '-'}
                  </td>
                  <td className="py-2 px-3">
                    {run.stepRuns.filter(s => s.status === 'completed').length}/{run.stepRuns.length}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Run detail modal */}
      {selectedRun && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Run Details</h3>
              <button onClick={() => setSelectedRun(null)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Workflow:</span>
                  <span className="ml-2 font-medium">{selectedRun.workflowName}</span>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <span className={`ml-2 px-2 py-0.5 text-xs rounded ${
                    selectedRun.status === 'completed' ? 'bg-green-100 text-green-700' :
                    selectedRun.status === 'running' ? 'bg-blue-100 text-blue-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {selectedRun.status}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Triggered By:</span>
                  <span className="ml-2">{selectedRun.triggeredBy}</span>
                </div>
                <div>
                  <span className="text-gray-500">Started:</span>
                  <span className="ml-2">{new Date(selectedRun.startedAt).toLocaleString()}</span>
                </div>
              </div>

              {selectedRun.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  {selectedRun.error}
                </div>
              )}

              <div>
                <h4 className="font-medium mb-2">Steps</h4>
                <div className="space-y-2">
                  {selectedRun.stepRuns.map((stepRun, i) => (
                    <div key={stepRun.stepId} className="p-3 bg-gray-50 rounded-md">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-gray-500">{i + 1}.</span>
                        <span className="font-medium">{stepRun.stepName}</span>
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          stepRun.status === 'completed' ? 'bg-green-100 text-green-700' :
                          stepRun.status === 'running' ? 'bg-blue-100 text-blue-700' :
                          stepRun.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {stepRun.status}
                        </span>
                        {stepRun.attempts > 1 && (
                          <span className="text-xs text-gray-500">(attempt {stepRun.attempts})</span>
                        )}
                      </div>
                      {stepRun.logs.length > 0 && (
                        <div className="ml-4 text-xs text-gray-600">
                          {stepRun.logs.slice(-2).map((log, j) => (
                            <div key={j} className={log.level === 'error' ? 'text-red-600' : ''}>
                              [{new Date(log.timestamp).toLocaleTimeString()}] {log.message}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
